/**
 * The icons that belong to this product rather than to an icon set.
 *
 * Both are drawn in the geometry of the Neuralk mark — right angles, square
 * corners, no flourish — and in the app's own visual language, where a GPU is
 * a small square. The generic lightning bolt they replace said "fast" about a
 * product whose honest promise is "a machine, when you need one".
 *
 * They match the stroke weight and the 24×24 box of the Tabler icons used
 * elsewhere, so they sit on the same line without looking imported.
 */

interface IconProps {
  /** Rendered size in pixels; the icon is square. */
  size?: number;
  className?: string;
}

/**
 * Four devices, one taken: what asking for GPUs actually does.
 *
 * The same square the fleet screen paints per GPU, so the button and the
 * machine it acts on speak the same language.
 */
export function IconGpus({ size = 16, className }: IconProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      {/* The one you are taking — filled, top left, where reading starts. */}
      <rect x="3" y="3" width="8" height="8" rx="1.5" fill="currentColor" stroke="none" />
      <rect x="13" y="3" width="8" height="8" rx="1.5" />
      <rect x="3" y="13" width="8" height="8" rx="1.5" />
      <rect x="13" y="13" width="8" height="8" rx="1.5" />
    </svg>
  );
}

/**
 * A machine being started: the box, and the two lines that say it is alive.
 *
 * Deliberately not a bolt. Starting a machine is the slow part of this product
 * — about six minutes — and an icon promising speed there would be a small lie
 * on the one screen where the researcher is already waiting.
 */
export function IconMachine({ size = 18, className }: IconProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <rect x="3" y="4" width="18" height="7" rx="1.5" />
      <rect x="3" y="13" width="18" height="7" rx="1.5" />
      <path d="M7 7.5h.01M7 16.5h.01" />
    </svg>
  );
}
